import { NextApiRequest } from 'next';
import { AxiosRequestConfig } from 'axios';
import { LivestreamState } from '#types/generated/graphql-types';

export const LivestreamLabelMap = {
  [LivestreamState.Upcoming]: 'livestreamStateLabelUpcoming',
  [LivestreamState.PreLive]: 'livestreamStateLabelUpcoming',
  [LivestreamState.Live]: 'livestreamStateLabelLive',
  [LivestreamState.OnHold]: 'livestreamStateLabelOnHold',
  [LivestreamState.Ended]: 'livestreamStateLabelEnded',
  [LivestreamState.Archived]: 'livestreamStateLabelArchived',
};
export const LivestreamStartTimeLabelMap = {
  [LivestreamState.Upcoming]: 'livestreamStartTimeUpcoming',
  [LivestreamState.PreLive]: 'livestreamStartTimeUpcoming',
  [LivestreamState.Live]: 'livestreamStartTimeLive',
  [LivestreamState.OnHold]: 'livestreamStartTimeLive',
  [LivestreamState.Ended]: 'livestreamStartTimeLive',
  [LivestreamState.Archived]: 'livestreamStartTimeLive',
};

export const languages = [
  {
    id: 'de',
    text: 'Deutsch',
    href: '/de',
  },
  {
    id: 'en',
    text: 'English',
    href: '/en',
  },
  {
    id: 'fr',
    text: 'Francais',
    href: '/fr',
  },
];

type ContentApiType = {
  [key: string]: {
    apiEndpoint: string;
    frontendRoute: string;
  };
};

export const contentType: ContentApiType = {
  Category: {
    apiEndpoint: 'categories',
    frontendRoute: 'categories',
  },
  Studio: {
    apiEndpoint: 'studios',
    frontendRoute: 'studios',
  },
};

export const mediaType = {
  Audio: {
    apiEndpoint: 'audios',
    frontendRoute: 'podcasts',
  },
  Series: {
    apiEndpoint: 'series',
    frontendRoute: 'series',
  },
  Video: {
    apiEndpoint: 'videos',
    frontendRoute: 'movies',
  },
};

// 6 is after 7 because of VOD-114, they changed the order when we added the new profiles
export const avatarIds = [1, 2, 3, 4, 5, 7, 6, 8, 9, 10, 11, 12];

// avatar image defaults to id 2 (ed euromaus)
export const avatarSource = (id: number, size: string) =>
  `/static/images/img/avatar-${avatarIds.includes(id) ? id : 2}${
    size ? `-${size}` : ''
  }.png`;
type ImageComponentRatios = {
  [key in ImageRendererTypes]: {
    primary: string;
    secondary?: string;
  };
};

export enum ImageRendererTypes {
  HeroItem = 'HeroItem',
  HeroItemLogo = 'HeroItemLogo',
  HeroTeaser = 'HeroTeaser',
  ItemAsset = 'ItemAsset',
  HeroTeaserLogo = 'HeroTeaserLogo',
  Teaser = 'Teaser',
  TeaserAudio = 'TeaserAudio',
  PosterTeaser = 'PosterTeaser',
  TeaserDetails = 'TeaserDetails',
  TeaserStretched = 'TeaserStretched',
  TeaserStudio = 'TeaserStudio',
  EpisodeListItem = 'EpisodeListItem',
}

export const imageRatioPerComponent: ImageComponentRatios = {
  [ImageRendererTypes.HeroItem]: { primary: '16_9', secondary: '3_5' },
  [ImageRendererTypes.HeroItemLogo]: { primary: '16_7', secondary: '1_1' },
  [ImageRendererTypes.HeroTeaser]: { primary: '16_7', secondary: '16_10' },
  [ImageRendererTypes.HeroTeaserLogo]: { primary: '16_7', secondary: '1_1' },
  [ImageRendererTypes.ItemAsset]: { primary: '3_4', secondary: '16_7' },
  [ImageRendererTypes.Teaser]: { primary: '16_10', secondary: '16_9' },
  [ImageRendererTypes.TeaserAudio]: { primary: '16_10', secondary: '16_9' },
  [ImageRendererTypes.PosterTeaser]: { primary: '3_4', secondary: '16_9' },
  [ImageRendererTypes.TeaserDetails]: { primary: '16_9' },
  [ImageRendererTypes.TeaserStretched]: { primary: '16_7', secondary: '3_5' },
  [ImageRendererTypes.TeaserStudio]: { primary: '16_7', secondary: '1_1' },
  [ImageRendererTypes.EpisodeListItem]: { primary: '16_9' },
};
export const itemsPerPage = 24;

export const placeholderImage = '/static/placeholder/16-9.jpg';
const placeholderImages = {
  '8_5': '/static/placeholder/8-5.jpg',
  '3_4': '/static/placeholder/3-4.jpg',
  '4_3': '/static/placeholder/4-3.jpg',
  '16_9': '/static/placeholder/16-9.jpg',
  '16_7': '/static/placeholder/16-7.jpg',
};

export const rolls = {
  preroll: 'PRE_ROLL',
  midroll: 'MID_ROLL',
  postroll: 'POST_ROLL',
};

export const staticTitle = 'VEEJOY - ';

export const axiosCreateOptions = (context, token): AxiosRequestConfig => ({
  baseURL: process.env.API_HOST,
  timeout: Number(process.env.API_TIMEOUT ?? 5000),
  headers: {
    Authorization: `Bearer ${token}`,
    Accept: 'application/ld+json',
    'Accept-Language':
      !context.locale || context.locale === 'default' ? 'de' : context.locale,
  },
});

export const axiosGetOptions = (url: string, page = 1): AxiosRequestConfig => ({
  method: 'GET',
  url,
  params: {
    itemsPerPage,
    page,
  },
});
export const knownFormatsHeightLabelMap: { [key: number]: string } = {
  360: 'SD',
  720: 'HD',
  1080: 'FULL HD',
  2160: 'UHD',
  1920: '4K UHD',
  1440: '2K QHD',
  960: 'FULL HD',
  640: 'HD',
  320: 'SD',
};

export interface IMediaBox {
  src?: string;
  ratio?: '16_7' | '16_9' | '16_10' | '3_4' | '3_5' | '4_3' | '8_5' | '1_1';
  alt?: string;
  srcSet?: string[];
}
export const getMediabox = (
  component: ImageRendererTypes,
  images,
  secondary = false,
  placeholderFallback = true
): IMediaBox => {
  let media = images;
  let mediabox: IMediaBox = {};
  let aspectRatio: string;
  if (secondary) {
    aspectRatio =
      imageRatioPerComponent[component].secondary ||
      imageRatioPerComponent[component].primary;
  } else {
    aspectRatio = imageRatioPerComponent[component].primary;
  }

  if (placeholderFallback && (!images || !images[aspectRatio])) {
    const placeholder = placeholderImages[aspectRatio] || placeholderImage;
    media = { [aspectRatio]: { aspectRatio, src: placeholder } };
  }

  if (media && media[aspectRatio]) {
    mediabox = {
      ...media[aspectRatio],
    };
  }

  return mediabox;
};
export const getProtocol = (req: NextApiRequest) =>
  req.headers['x-forwarded-proto'] === 'https' ? 'https://' : 'http://';
export const getCurrency = () => 'EUR';
